// Events map feature.
import { MapPinHoverIcon, MapPinIcon, createIllinoisMap } from './index.js';

export default function eventsMap() {
    let map;
    const eventsMapWrapper = document.getElementById('events-map-wrapper');
    const eventsMap = document.getElementById('events-map');
    if (!eventsMapWrapper) return;

    const {
        distance = null,
        filter = 'upcoming',
        page = 1,
        program = null,
        zip = null,
    } = eventsMapWrapper.dataset;

    Alpine.data('eventsMap', () => ({
        clearPins() {
            if (this.mapPins.length < 1) return;
            this.mapPins?.forEach((pin) => {
                if (!pin?.marker || pin.marker === null) return;
                pin.marker.map = null;
                pin.marker = null;
            });
            this.mapPins = [];
        },
        distance,
        events: [],
        get eventsList() {
            let previousMonth = '';
            // Attach a month/year over line to first event with a new month date.
            return this.events.map((event) => {
                if (event.featured) return event;
                let modifiedEvent = { ...event };
                if (event.eventMonth != previousMonth) {
                    modifiedEvent.overline = event.eventMonth;
                    previousMonth = event.eventMonth;
                }
                return modifiedEvent;
            });
        },
        fetchEvents() {
            const endpoint = new URL(
                '/api/events/list',
                window.location.origin
            );
            endpoint.searchParams.set('filter', this.filter);
            endpoint.searchParams.set('page', this.page);
            if (!this.hasFetched) {
                // Only fetch from start on first load.
                endpoint.searchParams.set('fromStart', true);
            }
            if (this.program) {
                endpoint.searchParams.set('program', this.program);
            }
            if (this.validZip) {
                endpoint.searchParams.set('zip', this.zip);
            }
            if (this.validZip && this.zip !== '' && this.distance) {
                endpoint.searchParams.set('distance', this.distance);
            }
            fetch(endpoint.toString())
                .then((response) => response.json())
                .then(({ events, pages }) => {
                    this.pages = pages;
                    if (this.page == 1) {
                        this.events = events;
                    } else {
                        const eventsList = [];
                        // Spreading results into an array can sometimes exhibit
                        // strange behavior by duplicating array items. This
                        // will check if an event already exists first.
                        const addToArray = (event) => {
                            if (eventsList.find((e) => e.id == event.id))
                                return;
                            eventsList.push(event);
                        };
                        this.events.forEach(addToArray);
                        events.forEach(addToArray);
                        this.events = eventsList;
                    }
                    this.hasFetched = true;
                });
        },
        fetchMap() {
            this.mapLoading = true;
            const endpoint = new URL('/api/events/map', window.location.origin);
            endpoint.searchParams.set('filter', this.filter);
            if (this.program) {
                endpoint.searchParams.set('program', this.program);
            }
            if (this.validZip) {
                endpoint.searchParams.set('zip', this.zip);
            }
            if (this.validZip && this.zip !== '' && this.distance) {
                endpoint.searchParams.set('distance', this.distance);
            }
            fetch(endpoint.toString())
                .then((response) => response.json())
                .then((json) => this.placePins(json));
        },
        filter,
        get hasEvents() {
            return this.events.length > 0;
        },
        initApp() {
            this.icon = MapPinIcon;
            this.fetchEvents();
            this.fetchMap();
            const refetch = () => {
                this.fetchEvents();
                this.fetchMap();
                this.updateQueryStringAction();
            };
            this.$watch('program', refetch);
            this.$watch('distance', refetch);
            this.$watch('zip', refetch);
        },
        hasFetched: false,
        get hasFilters() {
            return this.program || this.zip || this.distance;
        },
        get hasMore() {
            return this.page < this.pages;
        },
        mapLoading: false,
        mapPins: [],
        nextPage() {
            this.page++;
            Alpine.throttle(() => this.fetchEvents(), 500)();
            this.updateQueryStringAction();
        },
        page,
        pages: null,
        async placePins(mapPins) {
            const { AdvancedMarkerElement } = await google.maps.importLibrary(
                'marker'
            );

            this.clearPins();
            this.$nextTick(() => {
                this.mapPins = mapPins;
                this.mapPins.forEach((pin) => {
                    pin.marker = new AdvancedMarkerElement({
                        position: {
                            lat: pin.address.lat,
                            lng: pin.address.lng,
                        },
                        map,
                        content: MapPinIcon(),
                        title: pin.title,
                    });

                    pin.marker.addListener('click', () => {
                        document
                            .querySelector(`[data-event-id="${pin.id}"]`)
                            ?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                                inline: 'nearest',
                            });
                    });
                });
                this.mapLoading = false;
            });
        },
        program,
        resetFilters() {
            this.distance = null;
            this.page = 1;
            this.program = null;
            this.zip = null;

            this.fetchEvents();
            this.fetchMap();
            this.updateQueryStringAction();
        },
        updateFilter(filter) {
            this.filter = filter;
            this.page = 1;

            this.fetchEvents();
            this.fetchMap();
            this.updateQueryStringAction();
        },
        updateQueryStringAction() {
            const queryParams = new URLSearchParams();
            if (this.page > 1) queryParams.set('page', this.page);
            if (this.distance) queryParams.set('distance', this.distance);
            if (this.filter !== 'upcoming')
                queryParams.set('filter', this.filter);
            if (this.program) queryParams.set('program', this.program);
            if (this.zip) queryParams.set('zip', this.zip);
            history.replaceState(null, null, '?' + queryParams.toString());
        },
        get validZip() {
            return (
                this.zip &&
                this.zip.length === 5 &&
                /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(this.zip)
            );
        },
        zip,
        zoomIn() {
            map.setZoom(map.getZoom() + 1);
        },
        zoomOut() {
            map.setZoom(map.getZoom() - 1);
        },
    }));

    async function initMap() {
        map = await createIllinoisMap(google, eventsMap);
        window.dispatchEvent(new Event('map-loaded'));
    }

    initMap();
}
